import type { Locale } from 'date-fns';
import { format, setDefaultOptions } from 'date-fns';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_FNS_LOCALE_MAP, DEFAULT_LANGUAGE, Language, SUPPORTED_LANGUAGES } from '@/config/locale';
import {
	DATE_FORMATS,
	formatDate,
	formatDateDisplay,
	formatDateLong,
	formatDateMonthDayYear,
	formatDateRange,
	formatDateWithTime,
	formatMonthYear,
} from '@/lib/date-utils';

interface LocaleContextType {
	language: Language;
	dateLocale: Locale;
	changeLanguage: (lang: Language) => Promise<void>;
	supportedLanguages: Language[];
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

interface LocaleProviderProps {
	children: ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
	const { i18n } = useTranslation();
	const [language, setLanguage] = useState<Language>((i18n.language as Language) || DEFAULT_LANGUAGE);
	const supportedLanguages: Language[] = SUPPORTED_LANGUAGES;

	useEffect(() => {
		setDefaultOptions({
			locale: DATE_FNS_LOCALE_MAP[language],
			weekStartsOn: 1,
		});

		const handleLanguageChange = (lng: string) => {
			const next = (lng as Language) || DEFAULT_LANGUAGE;
			setLanguage(next);
			setDefaultOptions({
				locale: DATE_FNS_LOCALE_MAP[next],
				weekStartsOn: 1,
			});
		};

		i18n.on('languageChanged', handleLanguageChange);

		return () => {
			i18n.off('languageChanged', handleLanguageChange);
		};
	}, [i18n]);

	const changeLanguage = async (lang: Language) => {
		if (supportedLanguages.includes(lang)) {
			await i18n.changeLanguage(lang);
		}
	};

	return (
		<LocaleContext.Provider
			value={{
				language,
				dateLocale: DATE_FNS_LOCALE_MAP[language],
				changeLanguage,
				supportedLanguages,
			}}>
			{children}
		</LocaleContext.Provider>
	);
}

export function useLocale() {
	const context = useContext(LocaleContext);

	if (context === undefined) {
		throw new Error('useLocale must be used within a LocaleProvider');
	}

	return context;
}

export function useDateLocale(): Locale {
	return useLocale().dateLocale;
}

export function useDateFormatter() {
	const locale = useDateLocale();

	return {
		format: (date: Date | string | number, formatStr: string) => format(date, formatStr, { locale }),
		formatDateRange: (start: Date, end: Date) => formatDateRange(start, end, locale),
		formatDate: (date: Date | string | null | undefined, formatKey?: keyof typeof DATE_FORMATS) =>
			formatDate(date, formatKey, locale),
		formatMonthYear: (date: Date) => formatMonthYear(date, locale),
		formatDateDisplay: (date: Date | string | null | undefined) => formatDateDisplay(date, locale),
		formatDateLong: (date: Date | string | null | undefined) => formatDateLong(date, locale),
		formatDateWithTime: (date: Date | string | null | undefined) => formatDateWithTime(date, locale),
		formatDateMonthDayYear: (date: Date | string | null | undefined) => formatDateMonthDayYear(date, locale),
	};
}
